// src/components/usuarios/UsuarioList.tsx
import React, { useState, useEffect } from 'react';
import { api } from '../../api/client';
import UsuarioRoleEditor from './UsuarioRoleEditor';

interface Usuario {
    id: number;
    email: string;
    nombre: string;
    apellido: string;
    tipo_usuario: string;
    roles: string[];
    fecha_creacion: string;
    email_verificado: boolean;
    cuenta_bloqueada: boolean;
    // Token usage tracking
    total_tokens_used: number;
    total_openai_tokens: number;
    total_embedding_tokens: number;
    token_limit_monthly: number | null;
    token_limit_daily: number | null;
    tokens_last_reset: string | null;
}

export default function UsuarioList() {
    const [usuarios, setUsuarios] = useState<Usuario[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingUser, setEditingUser] = useState<Usuario | null>(null);
    const [showEditor, setShowEditor] = useState(false);

    useEffect(() => {
        loadUsuarios();
    }, []);

    const loadUsuarios = async () => {
        try {
            const data = await api.usuarios.list();
            console.log("data: ", data);
            setUsuarios(data);
        } catch (error) {
            console.error('Error al cargar usuarios:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (usuario: Usuario) => {
        setEditingUser(usuario);
        setShowEditor(true);
    };

    const handleSave = () => {
        setShowEditor(false);
        setEditingUser(null);
        loadUsuarios();
    };

    const handleCancel = () => {
        setShowEditor(false);
        setEditingUser(null);
    };

    const getRoleBadgeColor = (role: string) => {
        const colors: Record<string, string> = {
            admin: 'bg-red-600',
            shop: 'bg-blue-600',
            normas: 'bg-green-600',
        };
        return colors[role] || 'bg-gray-600';
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-xl text-gray-600">Cargando usuarios...</div>
            </div>
        );
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-gray-800">Gestión de Usuarios</h1>
                <div className="text-sm text-gray-600">
                    Total: {usuarios.length} usuarios
                </div>
            </div>

            {showEditor && editingUser && (
                <UsuarioRoleEditor
                    usuario={editingUser}
                    onSave={handleSave}
                    onCancel={handleCancel}
                />
            )}

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Usuario
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Email
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Tipo
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Roles
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Estado
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Uso de Tokens
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Acciones
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {usuarios.map((usuario) => (
                            <tr key={usuario.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">
                                        {usuario.nombre} {usuario.apellido}
                                    </div>
                                    <div className="text-sm text-gray-500">ID: {usuario.id}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">{usuario.email}</div>
                                    {usuario.email_verificado && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                            ✓ Verificado
                                        </span>
                                    )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="text-sm text-gray-900 capitalize">
                                        {usuario.tipo_usuario.replace('_', ' ')}
                                    </span>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="flex flex-wrap gap-1">
                                        {usuario.roles.map((role) => (
                                            <span
                                                key={role}
                                                className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium text-white ${getRoleBadgeColor(role)}`}
                                            >
                                                {role}
                                            </span>
                                        ))}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {usuario.cuenta_bloqueada ? (
                                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                                            🔒 Bloqueada
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                                            ✓ Activa
                                        </span>
                                    )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">
                                        <div className="font-medium">Total: {(usuario.total_tokens_used || 0).toLocaleString()}</div>
                                        <div className="text-xs text-gray-500">
                                            OpenAI: {(usuario.total_openai_tokens || 0).toLocaleString()} | Emb: {(usuario.total_embedding_tokens || 0).toLocaleString()}
                                        </div>
                                        {usuario.token_limit_monthly && (
                                            <div className="text-xs mt-1">
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded ${(usuario.total_tokens_used || 0) >= usuario.token_limit_monthly
                                                    ? 'bg-red-100 text-red-800'
                                                    : (usuario.total_tokens_used || 0) >= usuario.token_limit_monthly * 0.8
                                                        ? 'bg-yellow-100 text-yellow-800'
                                                        : 'bg-green-100 text-green-800'
                                                    }`}>
                                                    Límite: {usuario.token_limit_monthly.toLocaleString()}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    <button
                                        onClick={() => handleEdit(usuario)}
                                        className="text-blue-600 hover:text-blue-900 font-medium"
                                    >
                                        Editar Roles
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
